<template>
  <ValidationObserver slim v-slot="{ handleSubmit, invalid }" vid="phones" ref="observer">
    <BaseCard>
      <BaseCardHeader>
        <div class="flex justify-between items-baseline -mr-2">
          <h5 class="font-light">Phones</h5>
          <BaseIconButton
            v-if="!edit && $isAdminOrSuperAdmin"
            icon="edit"
            brand
            @click="edit = !edit"
          />
          <template v-else>
            <div class="space-x-2 justify-end">
              <BaseIconButton icon="times" silver @click="doRevert" />
              <BaseIconButton
                green
                icon="check"
                :disabled="invalid"
                @click="handleSubmit(doSubmit)"
              />
            </div>
          </template>
        </div>
      </BaseCardHeader>
      <BaseCardBody>
        <form @submit.prevent>
          <div class="-m-6">
            <SimpleList nested :entities="localAccountPhones">
              <template #header>
                <ListingHeader />
              </template>

              <template #default="{ index, entity }">
                <ListingItem
                  :index="index"
                  :entity="entity"
                  :edit="edit"
                  :updating="updating"
                  @change="updateAccountPhone(index, $event)"
                  @destroy="removeAccountPhone(index)"
                />
              </template>

              <template #body-after>
                <BaseListItem v-if="localAccountPhones.length === 0">
                  <div class="flex items-baseline -mx-4">
                    <div class="flex-1 px-4 text-center">Nothing to see yet!</div>
                  </div>
                </BaseListItem>
                <BaseListItem v-if="edit && !updating">
                  <div class="flex -mx-2 justify-end">
                    <BaseIconButton icon="plus" green @click="addNewAccountPhone" />
                  </div>
                </BaseListItem>
              </template>
            </SimpleList>
          </div>
        </form>
      </BaseCardBody>
    </BaseCard>
  </ValidationObserver>
</template>

<script>
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import ListingHeader from '@/components/AccountPhone/ListingHeader.vue';
import ListingItem from '@/components/AccountPhone/ListingItem.vue';
import SimpleList from '@/components/SimpleListFactory.vue';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';
import { entityComputed } from '@/store/helper';

const defaultEntity = {
  uuid: null,
  number: '',
  type: null,
};

export default {
  name: 'Phones',
  mixins: [veeValidateErrorsMixin],
  components: {
    SimpleList,
    ListingItem,
    ListingHeader,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      edit: false,
      updating: false,
      localAccountPhones: [],
    };
  },
  computed: {
    ...entityComputed,
    accountPhones() {
      const { phones: phoneIds = [] } = this.$currentAccount || {};
      return phoneIds.map((id) => this.entityById('accountPhones', id));
    },
  },
  watch: {
    accountPhones: {
      handler(newVal) {
        this.localAccountPhones = JSON.parse(JSON.stringify(newVal));
      },
      deep: true,
    },
  },
  created() {
    this.localAccountPhones = JSON.parse(JSON.stringify(this.accountPhones));
  },
  methods: {
    doRevert() {
      this.localAccountPhones = JSON.parse(JSON.stringify(this.accountPhones));
      this.edit = false;
    },
    async doSubmit() {
      const { dispatch } = this.$store;
      const { accountId } = this;
      const accountUpdatedData = { phones: [...this.localAccountPhones] };

      this.updating = true;

      const dispatchWithErrorHandling = this.handleFormErrors(dispatch);

      await dispatchWithErrorHandling('ui/accounts/updateAccount', {
        accountId,
        accountUpdatedData,
      });

      this.updating = false;
    },
    addNewAccountPhone() {
      this.localAccountPhones.push({ ...defaultEntity });
    },
    removeAccountPhone(index) {
      this.localAccountPhones.splice(index, 1);
    },
    updateAccountPhone(index, phone) {
      this.localAccountPhones.splice(index, 1, phone);
    },
    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            this.edit = false;
          })
          .catch(({ status = null, data } = {}) => {
            if (status === HTTP_UNPROCESSABLE_ENTITY) {
              this.observer = this.$refs.observer;
              this.addServerErrors(data.errors);
            } else {
              this.errorMessage = 'Something went wrong! Please try again';
            }
          });
    },
  },
};
</script>
